import { IconButton, Stack, SvgIcon, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import XIcon from '@mui/icons-material/X';
import type { SocialLinks } from '../../types';

/**
 * Compact icon strip for a provider's social-media links.
 *
 * Designed to be visually subtle so it never competes with the booking
 * CTA — small monochrome icons, only renders the platforms the provider
 * has actually filled in, and skips rendering entirely when none are
 * set (no empty container, no placeholder). Slot it into a sidebar or
 * beneath the bio.
 *
 * TikTok and Snapchat have no first-party MUI icon, so we inline brand
 * SVGs sized to match the surrounding @mui/icons-material set.
 */
type Props = {
  social: SocialLinks | null | undefined;
  /** "small" → 18px touch target, for sidebar use. "medium" → 24px. */
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
};

type IconCmp = React.ComponentType<React.ComponentProps<typeof SvgIcon>>;

const PLATFORM_META: Array<{
  key: keyof SocialLinks;
  label: string;
  icon: IconCmp;
}> = [
  { key: 'instagram', label: 'Instagram',  icon: InstagramIcon as IconCmp },
  { key: 'tiktok',    label: 'TikTok',     icon: TikTokIcon },
  { key: 'facebook',  label: 'Facebook',   icon: FacebookIcon as IconCmp },
  { key: 'snapchat',  label: 'Snapchat',   icon: SnapchatIcon },
  { key: 'youtube',   label: 'YouTube',    icon: YouTubeIcon as IconCmp },
  { key: 'twitter',   label: 'X / Twitter', icon: XIcon as IconCmp },
];

export function SocialMediaStrip({ social, size = 'small', sx }: Props) {
  if (!social) return null;
  const entries = PLATFORM_META.filter(
    (m) => typeof social[m.key] === 'string' && (social[m.key] as string).trim().length > 0,
  );
  if (entries.length === 0) return null;

  const iconSize = size === 'small' ? '1.1rem' : '1.35rem';

  return (
    <Stack direction="row" spacing={0.5} sx={sx}>
      {entries.map(({ key, label, icon: Icon }) => (
        <Tooltip key={key} title={label} arrow>
          <IconButton
            component="a"
            href={normaliseUrl(social[key] as string)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            size={size}
            sx={{
              color: 'text.secondary',
              transition: 'color 150ms ease, transform 150ms ease',
              '&:hover': {
                color: 'primary.main',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Icon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      ))}
    </Stack>
  );
}

/**
 * Some providers paste bare handles (e.g. "instagram.com/me" without the
 * scheme) — prefix with https:// so the anchor doesn't resolve relative
 * to the current site. We do NOT try to guess from handle-only inputs
 * (e.g. "@me"); the form coaches users to paste full URLs.
 */
function normaliseUrl(raw: string): string {
  const v = raw.trim();
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

// ── Brand SVGs (no first-party MUI icon) ─────────────────────

function TikTokIcon(props: React.ComponentProps<typeof SvgIcon>) {
  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.26z" />
    </SvgIcon>
  );
}

function SnapchatIcon(props: React.ComponentProps<typeof SvgIcon>) {
  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <path d="M12.166 2c4.273 0 4.953 3.184 4.953 5.262 0 1.42-.15 1.66-.15 2.512 0 .49.92.49 1.41.49.93 0 2.31-.43 2.66.4.27.66-.66 1.27-2.05 1.7-.46.14-1.05.42-1.05.84 0 .85 2.06 3.21 4.49 3.97.34.11.51.34.51.61 0 .55-1.18 1.06-3.27 1.34-.17.34-.28.93-.45 1.34-.07.17-.27.4-.6.4l-.13-.01c-.4-.06-.8-.16-1.39-.16-.41 0-.8.05-1.21.13-.84.17-1.51 1.34-3.95 1.34-2.44 0-3.11-1.17-3.95-1.34-.41-.08-.8-.13-1.21-.13-.59 0-1 .11-1.39.17l-.13.01c-.34 0-.54-.23-.6-.4-.17-.41-.28-1-.45-1.34-2.09-.28-3.27-.79-3.27-1.34 0-.27.17-.5.51-.61 2.43-.76 4.49-3.12 4.49-3.97 0-.42-.59-.7-1.05-.84-1.39-.43-2.32-1.04-2.05-1.7.35-.83 1.73-.4 2.66-.4.49 0 1.41 0 1.41-.49 0-.85-.15-1.09-.15-2.51C7.213 5.184 7.893 2 12.166 2z" />
    </SvgIcon>
  );
}
